import {useLayoutEffect} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {INSERT_HORIZONTAL_RULE_COMMAND} from '@lexical/react/LexicalHorizontalRuleNode';
import {FORMAT_ELEMENT_COMMAND, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND} from 'lexical';
import {
    INSERT_COMMENT,
    INSERT_TAB_SPACES,
    OPEN_LINK_MODAL_COMMAND,
    SET_TEXT_COLOR
} from '../commands';
import {SAVE_EDITOR_COMMAND} from './SavePlugin/commands';
import {isMacOS} from '../../../utils/platform';

type Props = {
    formatBulletList: () => void;
    formatNumberedList: () => void;
    insertImage: () => void;
    disabled?: boolean;
};

const isMAC = isMacOS();

export function ShortCodesPlugin({
    formatBulletList,
    formatNumberedList,
    insertImage,
    disabled
}: Props) {
    const [editor] = useLexicalComposerContext();

    useLayoutEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const ctrlKey = isMAC ? event.metaKey : event.ctrlKey;
            const isSpecialKey = ctrlKey || event.altKey;

            if (disabled) return;

            if (isSpecialKey && event.code === 'KeyS') {
                event.preventDefault();
                editor.dispatchCommand(SAVE_EDITOR_COMMAND, undefined);
            } else if (isSpecialKey && event.code === 'KeyH') {
                event.preventDefault();
                editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
            } else if (
                (ctrlKey && event.code === 'KeyK') ||
                (event.altKey && event.code === 'KeyA')
            ) {
                event.preventDefault();
                editor.dispatchCommand(INSERT_COMMENT, undefined);
            } else if (isSpecialKey && event.code === 'KeyL') {
                event.preventDefault();
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
            } else if (isSpecialKey && event.code === 'KeyM') {
                event.preventDefault();
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
            } else if (isSpecialKey && event.code === 'KeyR') {
                event.preventDefault();
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
            } else if (isSpecialKey && event.code === 'KeyP') {
                event.preventDefault();
                insertImage();
            } else if (event.altKey && event.code === 'KeyN') {
                event.preventDefault();
                formatNumberedList();
            } else if (event.altKey && event.code === 'KeyB') {
                event.preventDefault();
                formatBulletList();
            } else if (isSpecialKey && event.code === 'Comma') {
                event.preventDefault();
                editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
            } else if (isSpecialKey && event.code === 'Period') {
                event.preventDefault();
                editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
            } else if (isSpecialKey && event.code === 'KeyQ') {
                editor.dispatchCommand(OPEN_LINK_MODAL_COMMAND, null);
            } else if (isSpecialKey && event.code === 'KeyO') {
                event.preventDefault();
                editor.dispatchCommand(SET_TEXT_COLOR, undefined);
            } else if (event.code === 'Tab') {
                event.preventDefault();
                editor.dispatchCommand(INSERT_TAB_SPACES, undefined);
            }
        };

        return editor.registerRootListener(
            (rootElement: null | HTMLElement, prevRootElement: null | HTMLElement) => {
                if (prevRootElement !== null) {
                    prevRootElement.removeEventListener('keydown', onKeyDown);
                }
                if (rootElement !== null) {
                    rootElement.addEventListener('keydown', onKeyDown);
                }
            }
        );
    }, [editor, formatBulletList, formatNumberedList, insertImage, disabled]);

    return null;
}
